import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PackageSearch, Search, X, Clock, Calendar } from "lucide-react";
import { toast } from "sonner";

import {
    saveOrder,
    formatTotal,
    formatItemOptions,
    calculateDiscountedPrice,
    extractCustomerTagValue,
    extractCashDiscountPercent,
    extractCashDiscountTag,
    type OrderItem,
    type OrderRecord,
} from "@/lib/orders";
import {
    fetchCustomers,
    fetchProducts,
    fetchCustomerById,
    filterProductsByKeywords,
    getGroupNameById,
    parseDiscountPercentFromGroupName,
    PRODUCTS_FALLBACK_ERROR_MESSAGE,
    PRODUCTS_FALLBACK_TOAST_TITLE,
    PRODUCTS_FALLBACK_TOAST_DESC,
    type ProductRecord,
    submitOrder,
    type SubmitOrderPayload,
} from "@/lib/api";
import { getProductAvailability } from "@/lib/availability";
import { type ClientRecord } from "@/lib/mockClients";
import { getAuthenticatedUser } from "@/lib/auth";

import { EmptyState } from "@/components/feedback/EmptyState.tsx";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { CardSkeleton } from "@/components/loading/CardSkeleton.tsx";
import { PageBreadcrumb } from "@/components/layout/PageBreadcrumb.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ProductResultItem } from "./ProductResultItem";
import { OrderItemCard } from "./OrderItemCard";
import { ProductVariantModal } from "./ProductVariantModal";
import type { VariantProduct, VariantSelection } from "./ProductVariantModal";
import { Button } from "@/components/ui/button";

const fallbackProducts: ProductRecord[] = [
    { code: "GLS001", price: "EUR 156.00", numericPrice: 156 },
    { code: "GLS020", price: "EUR 116.00", numericPrice: 116 },
    { code: "GLS021", price: "EUR 124.00", numericPrice: 124 },
    { code: "GLTS021", price: "EUR 129.00", numericPrice: 129 },
];

interface NuovoOrdineProps {
    className?: string;
}

interface CatalogLocationState {
    preselectedProduct?: ProductRecord;
    selectedClientId?: string;
}

export function NuovoOrdine(props: NuovoOrdineProps) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props;

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [clients, setClients] = useState<ClientRecord[]>([]);
    const [selectedClientId, setSelectedClientId] = useState("");
    const [clientSearchQuery, setClientSearchQuery] = useState("");
    const [isClientSearchFocused, setIsClientSearchFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
    const [orderNotes, setOrderNotes] = useState("");
    const [products, setProducts] = useState<ProductRecord[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [productsError, setProductsError] = useState<string | null>(null);
    const [clientsError, setClientsError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Modal state
    const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
    const [selectedProductForVariant, setSelectedProductForVariant] =
        useState<VariantProduct | null>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as CatalogLocationState | null;
    const currentUser = useMemo(() => getAuthenticatedUser(), []);
    const agentTag = currentUser?.agentTag;
    const agentCode = currentUser?.agentCode;
    const activeAgent = agentTag || (agentCode ? `agente-${agentCode}` : "");

    const agentScopedClients = useMemo(() => {
        if (!agentTag && !agentCode) return clients.filter((c) => !c.isAgent);
        const tagLower = agentTag ? agentTag.toLowerCase().trim() : "";
        const codeStr = agentCode ? agentCode.trim().toLowerCase() : "";

        return clients.filter((c) => {
            if (c.isAgent === true) return false;
            const tags = (c.tags ?? []).map((t) => t.toLowerCase().trim());
            return tags.some((t) => {
                if (tagLower && t === tagLower) return true;
                if (
                    codeStr &&
                    (t === `agente-${codeStr}` || t === `agent-${codeStr}` || t === codeStr)
                )
                    return true;
                return false;
            });
        });
    }, [clients, agentTag, agentCode]);

    const matchedClients = useMemo(() => {
        const q = clientSearchQuery.trim().toLowerCase();
        if (!q) return agentScopedClients.slice(0, 15);
        return agentScopedClients
            .filter(
                (c) =>
                    c.name.toLowerCase().includes(q) ||
                    c.email.toLowerCase().includes(q) ||
                    (c.phone && c.phone.toLowerCase().includes(q)) ||
                    (c.city && c.city.toLowerCase().includes(q)) ||
                    (c.code && c.code.toLowerCase().includes(q)),
            )
            .slice(0, 15);
    }, [agentScopedClients, clientSearchQuery]);

    const selectedClient = useMemo(() => {
        return (
            agentScopedClients.find((c) => c.id === selectedClientId) ||
            clients.find((c) => c.id === selectedClientId)
        );
    }, [agentScopedClients, clients, selectedClientId]);

    const clientDiscountPercent = useMemo(() => {
        if (!selectedClient) return undefined;
        if (
            typeof selectedClient.discountPercent === "number" &&
            selectedClient.discountPercent > 0
        ) {
            return selectedClient.discountPercent;
        }
        if (selectedClient.groupName) {
            return parseDiscountPercentFromGroupName(selectedClient.groupName);
        }
        if (selectedClient.group) {
            const name = getGroupNameById(selectedClient.group);
            if (name) return parseDiscountPercentFromGroupName(name);
        }
        return undefined;
    }, [selectedClient]);

    const cashDiscountPercent = useMemo(() => {
        return extractCashDiscountPercent(selectedClient?.tags);
    }, [selectedClient?.tags]);

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        let isMounted = true;

        async function loadCustomers(): Promise<void> {
            setClientsError(null);

            try {
                const apiClients = await fetchCustomers(activeAgent);

                if (!isMounted || apiClients.length === 0) {
                    return;
                }

                setClients(apiClients);
                setSelectedClientId(
                    (currentValue) =>
                        currentValue || locationState?.selectedClientId || "",
                );
            } catch {
                if (!isMounted) {
                    return;
                }

                setClients([]);
                setClientsError("API clienti non disponibile al momento.");
                toast.error("Impossibile contattare il server", {
                    id: "nuovo-ordine-clients-error",
                    description:
                        "Non riesco a caricare i clienti per il nuovo ordine.",
                });
            }
        }

        void loadCustomers();

        return () => {
            isMounted = false;
        };
    }, [activeAgent]);

    // When a client is selected, ensure we have the full customer detail (including group and discountPercent)
    useEffect(() => {
        if (!selectedClientId) return;
        let isMounted = true;
        void (async () => {
            try {
                const detail = await fetchCustomerById(selectedClientId);
                if (isMounted && detail) {
                    setClients((prev) => {
                        const exists = prev.some((c) => c.id === selectedClientId);
                        if (exists) {
                            return prev.map((c) =>
                                c.id === selectedClientId ? { ...c, ...detail } : c,
                            );
                        }
                        return [...prev, detail];
                    });
                }
            } catch (err) {
                console.warn("[NuovoOrdine] Failed to fetch customer detail for group:", err);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [selectedClientId]);

    useEffect(() => {
        if (!isSearchFocused) {
            setIsLoadingProducts(false);
            setProductsError(null);
            return;
        }

        let isMounted = true;
        const timeoutId = window.setTimeout(() => {
            void (async () => {
                setIsLoadingProducts(true);
                setProductsError(null);

                try {
                    const res = await fetchProducts(searchQuery.trim());

                    if (!isMounted) {
                        return;
                    }

                    setProducts(res.products);
                } catch {
                    if (!isMounted) {
                        return;
                    }

                    setProducts(
                        filterProductsByKeywords(
                            fallbackProducts,
                            searchQuery.trim(),
                        ),
                    );
                    setProductsError(PRODUCTS_FALLBACK_ERROR_MESSAGE);
                    toast.warning(PRODUCTS_FALLBACK_TOAST_TITLE, {
                        id: "nuovo-ordine-products-fallback",
                        description: PRODUCTS_FALLBACK_TOAST_DESC,
                    });
                } finally {
                    if (isMounted) {
                        setIsLoadingProducts(false);
                    }
                }
            })();
        }, 250);

        return () => {
            isMounted = false;
            window.clearTimeout(timeoutId);
        };
    }, [isSearchFocused, searchQuery]);

    useEffect(() => {
        const preselectedProduct = locationState?.preselectedProduct;

        if (!preselectedProduct) {
            return;
        }

        const discountPercent = clientDiscountPercent;
        const discountedPrice = discountPercent
            ? calculateDiscountedPrice(preselectedProduct.numericPrice, discountPercent)
            : preselectedProduct.numericPrice;

        setSelectedProductForVariant({
            code: preselectedProduct.code,
            name: preselectedProduct.name,
            price: preselectedProduct.price,
            numericPrice: preselectedProduct.numericPrice,
            discountPercent: discountPercent,
            discountedPrice: discountedPrice,
            imageUrl: preselectedProduct.imageUrl,
            uid: preselectedProduct.uid,
            sku: preselectedProduct.sku || preselectedProduct.code,
            colors: preselectedProduct.colors,
            temperatures: preselectedProduct.temperatures,
            sizes: preselectedProduct.sizes,
            tags: preselectedProduct.tags,
            stockCount: preselectedProduct.stockCount,
            unlimited: preselectedProduct.unlimited,
        });
        setIsVariantModalOpen(true);
        navigate(location.pathname, { replace: true, state: null });
    }, [location.pathname, locationState, navigate, clientDiscountPercent]);

    // Recalculate order item prices if selected client (and thus discount) changes
    useEffect(() => {
        if (orderItems.length === 0) return;
        const discountPercent = clientDiscountPercent;
        setOrderItems((currentItems) =>
            currentItems.map((item) => {
                const basePrice = item.originalPrice ?? item.price;
                const newPrice = calculateDiscountedPrice(basePrice, discountPercent);
                return {
                    ...item,
                    originalPrice: basePrice,
                    discountPercent: discountPercent,
                    price: newPrice,
                };
            }),
        );
    }, [selectedClient?.id, clientDiscountPercent]);

    // -------------------------------------
    // Component functions
    // -------------------------------------

    const handleAddProductClick = (product: ProductRecord) => {
        const discountPercent = clientDiscountPercent;
        const discountedPrice = discountPercent
            ? calculateDiscountedPrice(product.numericPrice, discountPercent)
            : product.numericPrice;

        // Open the modal instead of adding directly
        setSelectedProductForVariant({
            code: product.code,
            name: product.name,
            price: product.price,
            numericPrice: product.numericPrice,
            discountPercent: discountPercent,
            discountedPrice: discountedPrice,
            imageUrl: product.imageUrl,
            uid: product.uid,
            sku: product.sku || product.code,
            colors: product.colors,
            temperatures: product.temperatures,
            sizes: product.sizes,
            tags: product.tags,
            stockCount: product.stockCount,
            unlimited: product.unlimited,
        });
        setIsVariantModalOpen(true);
        // Close the search dropdown
        setSearchQuery("");
        setIsSearchFocused(false);
    };

    const handleConfirmVariant = (
        product: VariantProduct,
        selection: VariantSelection,
    ) => {
        // Generate a unique ID based on product, variants and note
        const noteSuffix = selection.note ? `-${selection.note}` : "";
        const variantId = `${product.code}-${selection.color}-${selection.temperature}-${selection.size}${noteSuffix}`;

        const discountPercent = clientDiscountPercent;
        const unitPrice = discountPercent
            ? calculateDiscountedPrice(product.numericPrice, discountPercent)
            : product.numericPrice;

        const formattedOptions = formatItemOptions(
            {
                color: selection.color,
                temperature: selection.temperature,
                size: selection.size,
            },
            selection.note,
        );

        setOrderItems((currentItems) => {
            const existingIndex = currentItems.findIndex((item) => item.id === variantId);
            if (existingIndex >= 0) {
                return currentItems.map((item, idx) => {
                    if (idx !== existingIndex) return item;
                    return {
                        ...item,
                        quantity: item.quantity + selection.quantity,
                        price: unitPrice,
                        originalPrice: product.numericPrice,
                        discountPercent: discountPercent,
                        note: selection.note ? selection.note.trim() : undefined,
                        options: formattedOptions || item.options,
                    };
                });
            } else {
                const newItem: OrderItem = {
                    id: variantId,
                    code: product.code,
                    price: unitPrice,
                    originalPrice: product.numericPrice,
                    discountPercent: discountPercent,
                    quantity: selection.quantity,
                    imageUrl: product.imageUrl,
                    productUid: product.uid,
                    sku: product.sku || product.code,
                    title: product.name || product.code,
                    note: selection.note ? selection.note.trim() : undefined,
                    options: formattedOptions || undefined,
                    attributes: {
                        color: selection.color,
                        temperature: selection.temperature,
                        size: selection.size,
                    },
                    tags: product.tags,
                    stockCount: product.stockCount,
                    unlimited: product.unlimited,
                };
                return [...currentItems, newItem];
            }
        });

        // Close modal
        setIsVariantModalOpen(false);
        setSelectedProductForVariant(null);
    };

    const handleRemoveItem = (id: string) => {
        setOrderItems((currentItems) => currentItems.filter((item) => item.id !== id));
    };

    const handleUpdateQuantity = (id: string, newQuantity: number) => {
        if (newQuantity < 1) return;
        setOrderItems((currentItems) =>
            currentItems.map((item) =>
                item.id === id ? { ...item, quantity: newQuantity } : item,
            ),
        );
    };

    const handleUpdateNote = (id: string, newNote: string) => {
        setOrderItems((currentItems) =>
            currentItems.map((item) =>
                item.id === id
                    ? {
                          ...item,
                          note: newNote,
                          options:
                              formatItemOptions(item.attributes, newNote) ||
                              undefined,
                      }
                    : item,
            ),
        );
    };

    const handleCompleteOrder = async () => {
        if (!selectedClientId) {
            toast.error("Compilare tutti i campi obbligatori", {
                description:
                    "Seleziona un cliente prima di completare l'ordine.",
            });
            return;
        }

        if (orderItems.length === 0) {
            toast.error("Compilare tutti i campi obbligatori", {
                description:
                    "Aggiungi almeno un prodotto prima di completare l'ordine.",
            });
            return;
        }

        const client = selectedClient || clients.find((c) => c.id === selectedClientId);

        if (!client) {
            console.warn(
                "[NuovoOrdine] Unexpected state: no valid client selected to complete order",
            );
            toast.error("Impossibile creare l'ordine", {
                description:
                    "Seleziona un cliente valido per completare l'operazione.",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const finalNotes = orderNotes.trim();
            const effectiveCashDiscountPercent =
                cashDiscountPercent ?? extractCashDiscountPercent(client.tags);
            const calculatedCashDiscountAmount =
                effectiveCashDiscountPercent && effectiveCashDiscountPercent > 0
                    ? Number(((subtotal * effectiveCashDiscountPercent) / 100).toFixed(2))
                    : 0;
            const finalCouponCode =
                extractCashDiscountTag(client.tags) ||
                (effectiveCashDiscountPercent && effectiveCashDiscountPercent > 0
                    ? `cashdiscount-${effectiveCashDiscountPercent}`
                    : undefined);
            const finalCouponAmount =
                calculatedCashDiscountAmount > 0 ? calculatedCashDiscountAmount : undefined;

            const payload: SubmitOrderPayload = {
                customer: {
                    tsCommerceId: client.id,
                    fullName: client.name || "Cliente B2B",
                    email: client.email || "ordini@gealuce.com",
                    phone: client.phone?.trim() || "0000000000",
                    address: client.addressLine1?.trim() || "Via non specificata",
                    city: client.city?.trim() || "Sede",
                    zip: client.postalCode?.trim() || "00000",
                    state: client.province?.trim() || "IT",
                    country: client.country?.trim() || "IT",
                },
                items: orderItems.map((item) => {
                    const formattedOptions = item.options || formatItemOptions(item.attributes);
                    const itemNote = item.note?.trim() || undefined;
                    return {
                        productUid: item.productUid || 1,
                        sku: item.sku || item.code,
                        title: item.title || item.code,
                        quantity: item.quantity,
                        unitPrice: item.price,
                        options: formattedOptions || undefined,
                        customization: itemNote,
                        note: itemNote,
                        notes: itemNote,
                    };
                }),
                paymentMethod: "transfer",
                notes: finalNotes || undefined,
                message: finalNotes || undefined,
                couponCode: finalCouponCode,
                couponAmount: finalCouponAmount,
                cashDiscountPercent: effectiveCashDiscountPercent || undefined,
                cashDiscountAmount: finalCouponAmount,
                agent:
                    currentUser?.agentCode ||
                    (currentUser?.agentTag
                        ? currentUser.agentTag.replace(/^agente-/, "")
                        : null) ||
                    (client.tags ?? [])
                        .map((t) => t.match(/^(?:agente|agent)-([a-z0-9_-]+)$/i))
                        .find(Boolean)?.[1] ||
                    (currentUser?.id && currentUser.id !== "dev-nogravity"
                        ? currentUser.id
                        : undefined),
            };

            // Send to backend
            const result = await submitOrder(payload);
            const backendOrderId =
                result?.tsCommerceOrderId ||
                result?.id ||
                result?.orderId;

            if (!backendOrderId) {
                throw new Error("Il server non ha restituito un identificativo d'ordine valido.");
            }

            const orderDate = new Date().toISOString().slice(0, 10);

            const newOrder: OrderRecord = {
                id: backendOrderId,
                clientId: selectedClientId,
                date: orderDate,
                customerName: client.name,
                email: client.email,
                total: formattedTotal,
                subtotal: formattedSubtotal,
                cashDiscountPercent: cashDiscountPercent,
                cashDiscountAmount: cashDiscountPercent ? formattedCashDiscountAmount : undefined,
                status: 2,
                items: orderItems,
                notes: finalNotes,
                message: finalNotes,
                customerDiscountPercent: client.discountPercent,
                customerTags: client.tags,
                customerGroupName: client.groupName,
                paymentTag: extractCustomerTagValue(client.tags, "payment"),
                paydescriptionTag: extractCustomerTagValue(client.tags, "paydescription"),
            };

            saveOrder(newOrder);
            toast.success(`Ordine #${backendOrderId} registrato con successo`);
            navigate(`/ordine/${backendOrderId}`);
        } catch (error) {
            console.error("[NuovoOrdine] Order submission failed:", error);
            toast.error("Impossibile inviare l'ordine", {
                description:
                    "Errore durante il collegamento con il server. Riprova più tardi.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    const subtotal = useMemo(() => {
        return orderItems.reduce(
            (total, item) => total + item.price * item.quantity,
            0,
        );
    }, [orderItems]);

    const cashDiscountAmount = useMemo(() => {
        if (!cashDiscountPercent || cashDiscountPercent <= 0) return 0;
        return (subtotal * cashDiscountPercent) / 100;
    }, [subtotal, cashDiscountPercent]);

    const orderTotal = subtotal - cashDiscountAmount;
    const formattedSubtotal = formatTotal(subtotal);
    const formattedCashDiscountAmount = formatTotal(cashDiscountAmount);
    const formattedTotal = formatTotal(orderTotal);

    const restockingOrderItems = useMemo(() => {
        return orderItems
            .map((item) => ({
                item,
                availability: getProductAvailability({
                    tags: item.tags,
                    stockCount: item.stockCount,
                    unlimited: item.unlimited,
                }),
            }))
            .filter((entry) => entry.availability.caseType === "restocking");
    }, [orderItems]);

    return (
        <DashboardLayout className={className}>
            <div className="w-full">
                <PageBreadcrumb
                    items={[
                        { label: "Home", to: "/" },
                        { label: "Nuovo ordine" },
                    ]}
                />

                {/* Main Card */}
                <Card className="shadow-sm border-gray-100 min-h-125">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-xl sm:text-2xl font-bold">
                            Nuovo ordine
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-6 sm:px-6">
                        {clientsError && (
                            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                {clientsError}
                            </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
                            {/* Left Column: Cliente */}
                            <div className="space-y-2">
                                <Label
                                    htmlFor="cliente"
                                    className="text-sm font-medium text-gray-700">
                                    Cliente
                                </Label>
                                {selectedClient ? (
                                    <div className="flex items-start sm:items-center justify-between p-3.5 border border-blue-200 bg-blue-50/70 rounded-xl min-h-[58px] shadow-xs">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-semibold text-gray-900 truncate">
                                                {selectedClient.name}{" "}
                                                <span className="text-xs font-normal text-gray-500">
                                                    ({selectedClient.email})
                                                </span>
                                            </div>
                                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                                {(selectedClient.groupName || (selectedClient.group ? getGroupNameById(selectedClient.group) : undefined)) && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                                                        Gruppo: {selectedClient.groupName || getGroupNameById(selectedClient.group)}
                                                    </span>
                                                )}
                                                {cashDiscountPercent !== undefined && cashDiscountPercent > 0 && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                        Sconto piede: {cashDiscountPercent}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-gray-500 hover:text-red-600 shrink-0 h-8 px-2.5 ml-2 hover:bg-white/80"
                                            onClick={() =>
                                                setSelectedClientId("")
                                            }>
                                            <X className="w-3.5 h-3.5 mr-1" />
                                            Cambia
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Input
                                            id="cliente"
                                            value={clientSearchQuery}
                                            onChange={(e) =>
                                                setClientSearchQuery(
                                                    e.target.value,
                                                )
                                            }
                                            className="w-full pr-10 bg-white border-gray-200 h-9"
                                            placeholder="Cerca cliente affiliato per nome o email..."
                                            onFocus={() =>
                                                setIsClientSearchFocused(true)
                                            }
                                            onBlur={() => {
                                                setTimeout(
                                                    () =>
                                                        setIsClientSearchFocused(
                                                            false,
                                                        ),
                                                    200,
                                                );
                                            }}
                                        />
                                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

                                        {isClientSearchFocused && (
                                            <div className="absolute top-full left-0 right-0 mt-2 w-full max-h-60 bg-white border border-gray-200 rounded-xl shadow-xl ring-1 ring-black/5 z-20 overflow-y-auto overscroll-contain">
                                                {clientSearchQuery.trim().length === 0 && agentScopedClients.length > 0 && (
                                                    <div className="px-3 py-1.5 text-[11px] font-medium text-gray-500 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                                                        <span>Clienti affiliati ({agentScopedClients.length})</span>
                                                        <span className="text-gray-400">Digita per filtrare</span>
                                                    </div>
                                                )}
                                                {matchedClients.map(
                                                    (client) => (
                                                        <div
                                                            key={client.id}
                                                            className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 text-left transition-colors"
                                                            onMouseDown={() => {
                                                                setSelectedClientId(
                                                                    client.id,
                                                                );
                                                                setClientSearchQuery(
                                                                    "",
                                                                );
                                                            }}>
                                                            <div className="font-semibold text-gray-900 text-sm truncate">
                                                                {client.name}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {client.email}{" "}
                                                                {client.phone
                                                                    ? `• ${client.phone}`
                                                                    : ""}
                                                            </div>
                                                        </div>
                                                    ),
                                                )}
                                                {clientSearchQuery.trim()
                                                    .length > 0 &&
                                                    matchedClients.length ===
                                                        0 && (
                                                        <div className="p-3 text-xs text-gray-500 text-center">
                                                            Nessun cliente affiliato trovato
                                                        </div>
                                                    )}
                                                {agentScopedClients.length === 0 && (
                                                    <div className="p-3 text-xs text-gray-500 text-center">
                                                        Nessun cliente affiliato associato all&apos;agente
                                                    </div>
                                                )}
                                                {clientSearchQuery.trim().length === 0 && agentScopedClients.length > matchedClients.length && (
                                                    <div className="p-2 text-[11px] text-gray-400 text-center border-t border-gray-100">
                                                        Mostrati {matchedClients.length} di {agentScopedClients.length} clienti affiliati • Digita per cercare
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Right Column: Prodotti */}
                            <div className="space-y-2 relative">
                                <Label
                                    htmlFor="prodotti"
                                    className="text-sm font-medium text-gray-700">
                                    Prodotti
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="prodotti"
                                        value={searchQuery}
                                        onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                        }
                                        className="w-full pr-10 bg-white border-gray-200"
                                        placeholder="Cerca prodotti..."
                                        onFocus={() => setIsSearchFocused(true)}
                                        onBlur={() => {
                                            // Small delay to allow clicking on results before closing
                                            setTimeout(
                                                () => setIsSearchFocused(false),
                                                200,
                                            );
                                        }}
                                    />
                                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                </div>

                                {/* Search Results Dropdown */}
                                {searchQuery.length > 0 && isSearchFocused && (
                                    <div className="absolute top-full left-0 right-0 mt-2 w-full max-h-[min(24rem,50vh)] bg-white border border-gray-200 rounded-xl shadow-xl ring-1 ring-black/5 z-20 overflow-y-auto overscroll-contain">
                                        {productsError && (
                                            <div className="px-4 pt-4 text-xs text-amber-700">
                                                {productsError}
                                            </div>
                                        )}
                                        {isLoadingProducts && (
                                            <CardSkeleton count={4} />
                                        )}
                                        {!isLoadingProducts &&
                                            products.map((product) => (
                                                <ProductResultItem
                                                    key={product.code}
                                                    code={product.code}
                                                    name={product.name}
                                                    price={product.price}
                                                    numericPrice={
                                                        product.numericPrice
                                                    }
                                                    discountPercent={
                                                        clientDiscountPercent
                                                    }
                                                    imageUrl={product.imageUrl}
                                                    tags={product.tags}
                                                    stockCount={product.stockCount}
                                                    unlimited={product.unlimited}
                                                    onAdd={() =>
                                                        handleAddProductClick(
                                                            product,
                                                        )
                                                    }
                                                />
                                            ))}
                                        {!isLoadingProducts &&
                                            products.length === 0 && (
                                                <div className="p-3">
                                                    <EmptyState
                                                        icon={PackageSearch}
                                                        title="Nessun prodotto trovato"
                                                        description="Prova a usare un codice diverso o a semplificare la ricerca."
                                                        actionLabel="Pulisci ricerca"
                                                        onAction={() =>
                                                            setSearchQuery("")
                                                        }
                                                        iconClassName="bg-gray-100 text-gray-500"
                                                        className="rounded-xl px-4 py-8"
                                                    />
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Riepilogo ordine */}
                        {orderItems.length > 0 && (
                            <div className="mt-8 sm:mt-12 space-y-4">
                                <h3 className="text-lg font-bold text-gray-900 mb-4 sm:mb-6">
                                    Riepilogo ordine
                                </h3>

                                {/* Avviso articoli in riassortimento se presenti */}
                                {restockingOrderItems.length > 0 && (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50/90 p-4 space-y-3">
                                        <div className="flex items-center gap-2 text-amber-950 font-bold text-sm">
                                            <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                                            <span>
                                                Prodotti in riassortimento presenti nell'ordine ({restockingOrderItems.length})
                                            </span>
                                        </div>
                                        <div className="grid gap-2">
                                            {restockingOrderItems.map(({ item, availability }) => (
                                                <div
                                                    key={item.id}
                                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white/95 p-3 rounded-lg border border-amber-200/80 text-xs gap-1.5 sm:gap-2 shadow-xs"
                                                >
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-bold text-gray-900">
                                                            {item.code}
                                                        </span>
                                                        {item.title && item.title !== item.code && (
                                                            <span className="text-gray-600 text-xs font-medium">
                                                                - {item.title}
                                                            </span>
                                                        )}
                                                        <span className="text-gray-500 font-medium">
                                                            (Qtà: {item.quantity})
                                                        </span>
                                                        {item.attributes.color && (
                                                            <span className="text-gray-500 text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">
                                                                {item.attributes.color}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-amber-950 font-medium bg-amber-50/80 px-2.5 py-1 rounded border border-amber-200/60">
                                                        <Calendar className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                                        <span>
                                                            Data presunto arrivo:{" "}
                                                            <strong className="text-amber-950 font-bold">
                                                                {availability.restockDate || "In attesa di conferma"}
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    {orderItems.map((item) => (
                                        <OrderItemCard
                                            key={item.id}
                                            id={item.id}
                                            code={item.code}
                                            title={item.title}
                                            price={item.price}
                                            originalPrice={item.originalPrice}
                                            discountPercent={item.discountPercent}
                                            quantity={item.quantity}
                                            imageUrl={item.imageUrl}
                                            attributes={item.attributes}
                                            tags={item.tags}
                                            stockCount={item.stockCount}
                                            unlimited={item.unlimited}
                                            note={item.note || ""}
                                            onRemove={handleRemoveItem}
                                            onUpdateQuantity={
                                                handleUpdateQuantity
                                            }
                                            onUpdateNote={handleUpdateNote}
                                        />
                                    ))}
                                </div>

                                {/* Note Generali Ordine */}
                                <div className="space-y-2 pt-4 border-t border-gray-100">
                                    <label htmlFor="order-general-notes" className="text-sm font-semibold text-gray-900 block">
                                        Note generali per l'ordine (opzionale)
                                    </label>
                                    <textarea
                                        id="order-general-notes"
                                        rows={3}
                                        value={orderNotes}
                                        onChange={(e) => setOrderNotes(e.target.value)}
                                        placeholder="Inserisci eventuali istruzioni di consegna o note generali per l'intero ordine..."
                                        className="w-full text-xs rounded-lg border border-gray-200 bg-white p-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                                    />
                                </div>

                                {cashDiscountPercent !== undefined && cashDiscountPercent > 0 ? (
                                    <div className="space-y-2.5 py-4 border-b border-gray-100 mt-4">
                                        <div className="flex items-center justify-between text-sm text-gray-600">
                                            <span>Subtotale articoli</span>
                                            <span className="font-medium text-gray-900">{formattedSubtotal}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm text-emerald-700">
                                            <span className="flex items-center gap-1.5">
                                                <span>Sconto piede</span>
                                                <span className="text-[11px] font-semibold bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">
                                                    -{cashDiscountPercent}%
                                                </span>
                                            </span>
                                            <span className="font-semibold">-{formattedCashDiscountAmount}</span>
                                        </div>
                                        <div className="flex items-center justify-between pt-2 border-t border-gray-100 font-bold text-gray-900">
                                            <span className="text-base">Totale ordine</span>
                                            <span className="text-xl text-right">{formattedTotal}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-start sm:items-center justify-between gap-3 py-6 border-b border-gray-100 mt-4">
                                        <span className="font-bold text-gray-900">
                                            Totale
                                        </span>
                                        <span className="font-bold text-gray-900 text-lg text-right">
                                            {formattedTotal}
                                        </span>
                                    </div>
                                )}

                                <div className="flex justify-center pt-4">
                                    <Button
                                        className="w-full sm:w-auto bg-[#0D6EFD] hover:bg-blue-700 text-white px-8 h-10 font-medium"
                                        onClick={handleCompleteOrder}
                                        disabled={isSubmitting}>
                                        {isSubmitting
                                            ? "Invio in corso..."
                                            : "Completa l'ordine"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Variant Selection Modal */}
                <ProductVariantModal
                    isOpen={isVariantModalOpen}
                    onOpenChange={setIsVariantModalOpen}
                    product={selectedProductForVariant}
                    onConfirm={handleConfirmVariant}
                />
            </div>
        </DashboardLayout>
    );
}
